import React, { useEffect, useRef, useState } from "react";
import { useAPI } from "../../contexts/APIContext";
import { showToast } from "../Toast";
import logger from "../../utils/logger";

/**
 * Internal-only Storyverse discovery toggle.
 *
 * Always asks the backend (JWT required). Never infers access from client email.
 * Accepts optional story-detail hints: can_moderate_storyverse / show_in_storyverse.
 */
export default function StoryverseModerationToggle({
  storyId,
  enabled = true,
  canModerateStoryverse,
  showInStoryverse: showInStoryverseProp,
}) {
  const { apiClient } = useAPI();
  const [canModerate, setCanModerate] = useState(
    canModerateStoryverse === true,
  );
  const [showInStoryverse, setShowInStoryverse] = useState(
    showInStoryverseProp !== false,
  );
  const [loadingPermission, setLoadingPermission] = useState(
    enabled && canModerateStoryverse !== true,
  );
  const [saving, setSaving] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    if (canModerateStoryverse === true) {
      setCanModerate(true);
      setShowInStoryverse(showInStoryverseProp !== false);
      setLoadingPermission(false);
    }
  }, [canModerateStoryverse, showInStoryverseProp]);

  useEffect(() => {
    if (!enabled || !apiClient || !storyId) {
      setCanModerate(false);
      setLoadingPermission(false);
      return undefined;
    }

    // Story detail already granted access — no extra round-trip.
    if (canModerateStoryverse === true) {
      return undefined;
    }

    if (typeof apiClient.getStoryverseVisibility !== "function") {
      logger.warn(
        "getStoryverseVisibility missing on apiClient — hard refresh the page",
      );
      setCanModerate(false);
      setLoadingPermission(false);
      return undefined;
    }

    const seq = ++requestSeq.current;
    setLoadingPermission(true);

    (async () => {
      try {
        const resp = await apiClient.getStoryverseVisibility(storyId);
        if (seq !== requestSeq.current) return;

        const data = resp?.data;
        const allowed =
          data?.can_moderate_storyverse === true ||
          (resp?.status === "success" &&
            data != null &&
            Object.prototype.hasOwnProperty.call(data, "show_in_storyverse"));

        if (allowed) {
          setCanModerate(true);
          setShowInStoryverse(data.show_in_storyverse !== false);
          logger.log("Storyverse moderation enabled", {
            storyId,
            show_in_storyverse: data.show_in_storyverse !== false,
          });
        } else {
          setCanModerate(false);
          logger.log("Storyverse moderation denied", {
            storyId,
            status: resp?.status,
            error: resp?.error || resp?.detail,
          });
        }
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setCanModerate(false);
        logger.log("Storyverse moderation unavailable", err?.message || err);
      } finally {
        if (seq === requestSeq.current) {
          setLoadingPermission(false);
        }
      }
    })();

    return undefined;
  }, [apiClient, storyId, enabled, canModerateStoryverse]);

  const handleToggle = async () => {
    if (!apiClient || !storyId || saving || !canModerate) return;
    if (typeof apiClient.setStoryverseVisibility !== "function") {
      showToast("Please hard-refresh the page and try again", "error");
      return;
    }

    const next = !showInStoryverse;
    setSaving(true);
    try {
      const resp = await apiClient.setStoryverseVisibility(storyId, next);
      if (resp?.status === "success") {
        setShowInStoryverse(next);
        if (typeof apiClient.invalidateStoryverseDiscoveryCaches === "function") {
          apiClient.invalidateStoryverseDiscoveryCaches();
        }
        showToast(
          next
            ? "Shown in Storyverse — shelves will refresh"
            : "Hidden from Storyverse — shelves will refresh",
          "success",
        );
      } else {
        showToast(resp?.error || "Failed to update Storyverse visibility", "error");
      }
    } catch (err) {
      logger.error("Failed to update Storyverse visibility:", err);
      showToast("Failed to update Storyverse visibility", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!enabled || loadingPermission || !canModerate) {
    return null;
  }

  return (
    <label
      data-testid="storyverse-moderation-toggle"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "6px 14px",
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 700,
        background: showInStoryverse ? "#E6F7EE" : "#FFE9EE",
        color: showInStoryverse ? "#20A96A" : "#E53E3E",
        boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
        cursor: saving ? "wait" : "pointer",
        opacity: saving ? 0.7 : 1,
        userSelect: "none",
      }}
    >
      <input
        type="checkbox"
        checked={showInStoryverse}
        disabled={saving}
        onChange={handleToggle}
        aria-label="Show in Storyverse"
        style={{ width: 16, height: 16, cursor: saving ? "wait" : "pointer" }}
      />
      <span>{saving ? "Saving…" : "Show in Storyverse"}</span>
    </label>
  );
}
